import { routes, type RouteConfig } from "@/app/router/routes";
import { useUiStore } from "@/app/store/useUiStore";
import { Button } from "@/shared/components/ui/button";
import { ScrollArea } from "@/shared/components/ui/scroll-area";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { getAppEnv } from "@/shared/libs/env";
import { cn } from "@/shared/libs/utils";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";

function isPathActive(currentPathname: string, targetPath?: string) {
  if (!targetPath) return false;
  if (targetPath === "/") return currentPathname === "/";
  return currentPathname === targetPath || currentPathname.startsWith(`${targetPath}/`);
}

function isRouteActive(currentPathname: string, route: RouteConfig) {
  if (route.children?.length) return route.children.some((c) => isPathActive(currentPathname, c.path));
  return isPathActive(currentPathname, route.path);
}

function isRouteVisibleInEnv(route: RouteConfig): boolean {
  if (!route.onlyEnv) return true;
  const env = getAppEnv();
  return env === "local" || env === route.onlyEnv;
}

function filterRoutesByEnv(routeList: RouteConfig[]): RouteConfig[] {
  return routeList
    .filter(isRouteVisibleInEnv)
    .map((r) =>
      r.children ? { ...r, children: r.children.filter(isRouteVisibleInEnv) } : r
    )
    .filter((r) => !r.children || r.children.length > 0);
}

export function Sidebar() {
  const location = useLocation();
  const { isSidebarCollapsed, toggleSidebarCollapse } = useUiStore();

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  const visibleRoutes = useMemo(() => filterRoutesByEnv(routes), []);

  const defaultOpenGroups = useMemo(() => {
    const next: Record<string, boolean> = {};
    for (const r of visibleRoutes) {
      if (r.children?.length && isRouteActive(location.pathname, r)) next[r.id] = true;
    }
    return next;
  }, [location.pathname, visibleRoutes]);

  const mergedOpenGroups = { ...defaultOpenGroups, ...openGroups };

  const toggleGroup = (id: string) => {
    setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <aside
      className={cn(
        "fixed left-0 top-0 z-40 h-screen border-r bg-sidebar transition-all duration-300",
        isSidebarCollapsed ? "w-16" : "w-64"
      )}
    >
      {/* Logo */}
      <div className="flex h-16 items-center justify-between border-b px-4">
        {!isSidebarCollapsed && (
          <Link to="/" className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg  text-primary-foreground font-bold">
              <img src="/prism_logo.png" alt="BackOffice Control Panel" className="h-6 w-6 object-contain" />
            </div>
            <span className="text-lg font-semibold text-sidebar-foreground">Prism BackOffice</span>
          </Link>
        )}
        {isSidebarCollapsed && (
          <Link to="/" className="mx-auto">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold">
              B
            </div>
          </Link>
        )}
      </div>

      {/* Navigation */}
      <ScrollArea className="h-[calc(100vh-8rem)]">
        <nav className="space-y-1 p-2">
          <TooltipProvider delayDuration={0}>
            {visibleRoutes.map((route) => {
              const Icon = route.icon;
              const active = isRouteActive(location.pathname, route);
              const hasChildren = !!route.children?.length;

              // Collapsed: 아이콘만 + Tooltip(그룹이면 하위 링크 목록 제공)
              if (isSidebarCollapsed) {
                return (
                  <Tooltip key={route.id}>
                    <TooltipTrigger asChild>
                      {hasChildren ? (
                        <Button
                          type="button"
                          variant={active ? "secondary" : "ghost"}
                          className={cn(
                            "w-full justify-center",
                            active && "bg-sidebar-accent text-sidebar-accent-foreground"
                          )}
                          size="icon"
                        >
                          {Icon && <Icon className="h-5 w-5" />}
                        </Button>
                      ) : (
                        <Link to={route.path ?? "#"}>
                          <Button
                            variant={active ? "secondary" : "ghost"}
                            className={cn(
                              "w-full justify-center",
                              active && "bg-sidebar-accent text-sidebar-accent-foreground"
                            )}
                            size="icon"
                          >
                            {Icon && <Icon className="h-5 w-5" />}
                          </Button>
                        </Link>
                      )}
                    </TooltipTrigger>

                    <TooltipContent side="right" className="p-2">
                      <div className="text-sm font-medium">{route.name}</div>

                      {hasChildren && (
                        <div className="mt-2 flex flex-col gap-1">
                          {route.children!.map((child) => {
                            const childActive = isPathActive(location.pathname, child.path);
                            return (
                              <Link
                                key={child.id}
                                to={child.path!}
                                className={cn(
                                  "rounded px-2 py-1 text-sm hover:bg-muted",
                                  childActive && "bg-muted font-medium"
                                )}
                              >
                                {child.name}
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </TooltipContent>
                  </Tooltip>
                );
              }

              // Expanded
              if (!hasChildren) {
                return (
                  <Link key={route.id} to={route.path ?? "#"}>
                    <Button
                      variant={active ? "secondary" : "ghost"}
                      className={cn(
                        "w-full justify-start gap-3",
                        active && "bg-sidebar-accent text-sidebar-accent-foreground"
                      )}
                    >
                      {Icon && <Icon className="h-5 w-5" />}
                      <span>{route.name}</span>
                    </Button>
                  </Link>
                );
              }

              const isOpen = !!mergedOpenGroups[route.id];

              return (
                <div key={route.id} className="space-y-1">
                  <Button
                    type="button"
                    variant={active ? "secondary" : "ghost"}
                    className={cn(
                      "w-full justify-between gap-3",
                      active && "bg-sidebar-accent text-sidebar-accent-foreground"
                    )}
                    onClick={() => toggleGroup(route.id)}
                  >
                    <span className="flex items-center gap-3">
                      {Icon && <Icon className="h-5 w-5" />}
                      <span>{route.name}</span>
                    </span>

                    <ChevronDown className={cn("h-4 w-4 transition-transform", isOpen && "rotate-180")} />
                  </Button>

                  {isOpen && (
                    <div className="space-y-1 pl-3">
                      {route.children!.map((child) => {
                        const childActive = isPathActive(location.pathname, child.path);
                        return (
                          <Link key={child.id} to={child.path!}>
                            <Button
                              variant={childActive ? "secondary" : "ghost"}
                              className={cn(
                                "w-full justify-start",
                                childActive && "bg-sidebar-accent text-sidebar-accent-foreground"
                              )}
                              size="sm"
                            >
                              <span className="pl-6">{child.name}</span>
                            </Button>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </TooltipProvider>
        </nav>
      </ScrollArea>

      {/* Collapse Toggle */}
      <div className="absolute bottom-0 left-0 right-0 border-t p-2">
        <Button
          variant="ghost"
          size={isSidebarCollapsed ? "icon" : "sm"}
          className={cn("w-full", !isSidebarCollapsed && "justify-start gap-2")}
          onClick={toggleSidebarCollapse}
        >
          {isSidebarCollapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <>
              <ChevronLeft className="h-4 w-4" />
              <span>접기</span>
            </>
          )}
        </Button>
      </div>
    </aside>
  );
}